// @flow
import React from 'react';
import DOMPurify from 'dompurify';
import Group from 'sail/Group';
import classNames from 'classnames';
import {ButtonLink} from 'sail/Button';
import Icon from 'sail/Icon';
import Box from 'sail/Box';
import {UnstyledLink} from 'sail/UnstyledLink';
import {Caption} from 'sail/Text';
import copy from 'copy-to-clipboard';
import {useToast} from 'docs/hooks';
import './Search.scss';

import type {
  SearchResult as SearchResultType,
  CopyableSearchResult as CopyableSearchResultType,
} from 'docs/data';

type onSearchResultClickType = (result: SearchResultType) => Promise<void>;

/* eslint-disable react/no-danger */

const MethodSignature = ({result}: {result: SearchResultType}) => {
  if (result.methodSignature) {
    const signature =
      result.type === 'jsref'
        ? result.methodSignature.split(',').join(', ')
        : result.methodSignature;
    return <p className="SearchResult-methodSignature">{signature}</p>;
  } else {
    return null;
  }
};

const Breadcrumbs = ({result}: {result: SearchResultType}) => {
  if (result.type === 'doc' && result.breadcrumbs) {
    return (
      <Caption color="gray" className="SearchResult-breadcrumbs">
        <span
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(result.breadcrumbs),
          }}
        />
      </Caption>
    );
  } else {
    return null;
  }
};

type CopyableSearchResultProps = {
  result: CopyableSearchResultType,
  isSelected: boolean,
  onSearchResultClick: onSearchResultClickType,
  onMouseOver: () => void,
};

const CopyableSearchResult = ({
  result,
  isSelected,
  onMouseOver,
  onSearchResultClick,
}: CopyableSearchResultProps) => {
  const [toast, showToast] = useToast();

  const className = classNames('SearchResult', 'SearchResult-copyable', {
    'SearchResult-selected': isSelected,
  });

  return (
    <Box
      flex={{alignItems: 'center'}}
      onMouseOver={onMouseOver}
      onClick={() => {
        copy(result.content);
        showToast('Copied!');
        onSearchResultClick(result);
      }}
      className={className}
    >
      <Group spacing={2} className="SearchResult-left-wrapper">
        <ButtonLink
          label={<span className="SearchResult-title">{result.title}</span>}
        />
        <div className="SearchResult-methodSignature">{result.content}</div>
      </Group>
      <div className="SearchResult-right-wrapper">
        {toast ? (
          <Box className="SearchResult-right-wrapper-toast">{toast}</Box>
        ) : (
          <Icon icon="clipboard" color="blue" />
        )}
      </div>
    </Box>
  );
};

type SearchResultProps = {
  result: SearchResultType,
  isSelected: boolean,
  onSearchResultClick: onSearchResultClickType,
  onMouseOver: () => void,
};

export const SearchResult = ({
  result,
  isSelected,
  onMouseOver,
  onSearchResultClick,
}: SearchResultProps) => {
  if (result.type === 'copyable') {
    return (
      <CopyableSearchResult
        result={result}
        isSelected={isSelected}
        onMouseOver={onMouseOver}
        onSearchResultClick={onSearchResultClick}
      />
    );
  }

  const className = classNames('SearchResult', {
    'SearchResult-selected': isSelected,
  });

  if (result.type === 'shortcut') {
    return (
      <Box
        onMouseOver={onMouseOver}
        onClick={(evt) => {
          evt.preventDefault();
          onSearchResultClick(result);
        }}
        className={className}
      >
        <Group spacing={2} className="SearchResult-left-wrapper">
          <ButtonLink
            label={<span className="SearchResult-title">{result.title}</span>}
            wrap
          />
        </Group>
      </Box>
    );
  }

  const {isExternal, title, content, url} = result;

  return (
    <UnstyledLink
      onMouseOver={onMouseOver}
      href={url}
      onClick={(evt) => {
        evt.preventDefault();
        onSearchResultClick(result);
      }}
      className={className}
      target={isExternal ? '_blank' : undefined}
    >
      <Group spacing={2} className="SearchResult-left-wrapper">
        <ButtonLink
          label={
            <span
              className="SearchResult-title"
              dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(title)}}
            />
          }
          wrap
        />
        <MethodSignature result={result} />
        <div className="SearchResult-description">
          <span
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(content),
            }}
          />
        </div>
        <Breadcrumbs result={result} />
      </Group>
      <div className="SearchResult-right-wrapper">
        {isExternal && <Icon icon="external" color="gray" size={12} />}
      </div>
    </UnstyledLink>
  );
};
